// src/pages/GiftCheckout.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Users, MapPin } from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import { useCart } from "../context/CartContext";
import Currency from "../components/Currency";
import { useCurrency } from "../context/CurrencyContext";

export default function GiftCheckout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartTotal, clearCart } = useCart();
  const { format } = useCurrency();
  const fileInputRef = useRef(null);

  const singleGift = location.state?.gift || null;
  const isCartMode = location.state?.cartMode === true;

  // ── SINGLE GIFT MODE (existing "Buy" flow — unchanged) ──
  const [singleForm, setSingleForm] = useState({
    senderName: "", receiverName: "", address: "", city: "",
    state: "", zipCode: "", phoneNumber: "", country: "",
    apartmentNumber: "", note: "",
  });

  // ── CART MODE ──
  const [addressMode, setAddressMode] = useState("same"); // "same" | "different"
  const [senderName, setSenderName] = useState("");
  const [sameAddressForm, setSameAddressForm] = useState({
    receiverName: "", address: "", city: "", state: "",
    zipCode: "", phoneNumber: "", country: "", apartmentNumber: "", note: "",
  });
  const [itemForms, setItemForms] = useState({});

  useEffect(() => {
    if (isCartMode && cart.length > 0) {
      const initial = {};
      cart.forEach(({ gift }) => {
        initial[gift._id] = {
          receiverName: "", address: "", city: "", state: "",
          zipCode: "", phoneNumber: "", country: "", apartmentNumber: "", note: "",
        };
      });
      setItemForms(initial);
    }
  }, [isCartMode, cart]);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!singleGift && (!isCartMode || cart.length === 0)) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          {isCartMode ? "Your cart is empty." : "No gift selected."}
        </p>
        <Link to="/dashboard/gifts" className="text-purple-600 dark:text-purple-400 font-medium hover:underline">
          ← Back to Gifts
        </Link>
      </div>
    );
  }

  const setSingle = (field) => (e) => setSingleForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setSame = (field) => (e) => setSameAddressForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setItemField = (giftId, field) => (e) =>
    setItemForms((prev) => ({ ...prev, [giftId]: { ...prev[giftId], [field]: e.target.value } }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── SUBMIT: single gift (unchanged behavior) ──
  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    const required = ["senderName", "receiverName", "address", "city", "state", "phoneNumber", "country"];
    for (const field of required) {
      if (!singleForm[field].trim()) {
        toast.error("Please fill in all required fields");
        return;
      }
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("giftId", singleGift._id);
      Object.entries(singleForm).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append("image", imageFile);

      const res = await api.post("/gift-orders", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        toast.success("Gift order placed successfully!");
        navigate("/dashboard/gift-orders");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  // ── SUBMIT: cart (same address OR different addresses) ──
  const handleCartSubmit = async (e) => {
    e.preventDefault();

    if (!senderName.trim()) {
      toast.error("Please enter the sender's name");
      return;
    }

    let items;
    if (addressMode === "same") {
      const required = ["receiverName", "address", "city", "state", "phoneNumber", "country"];
      for (const field of required) {
        if (!sameAddressForm[field].trim()) {
          toast.error("Please fill in all required address fields");
          return;
        }
      }
      items = cart.map(({ gift, quantity }) => ({ giftId: gift._id, quantity }));
    } else {
      for (const { gift } of cart) {
        const f = itemForms[gift._id];
        const required = ["receiverName", "address", "city", "state", "phoneNumber", "country"];
        for (const field of required) {
          if (!f || !f[field].trim()) {
            toast.error(`Please fill in all required fields for "${gift.title}"`);
            return;
          }
        }
      }
      items = cart.map(({ gift, quantity }) => ({
        giftId: gift._id,
        quantity,
        ...itemForms[gift._id],
      }));
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("orderType", addressMode === "same" ? "cart_same_address" : "cart_multiple_addresses");
      fd.append("senderName", senderName);
      fd.append("itemsJson", JSON.stringify(items));

      if (addressMode === "same") {
        Object.entries(sameAddressForm).forEach(([k, v]) => fd.append(k, v));
      }
      if (imageFile) fd.append("image", imageFile);

      const res = await api.post("/gift-orders/cart", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success) {
        toast.success("Gift order placed successfully!");
        clearCart();
        navigate("/dashboard/gift-orders");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full px-3.5 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white";
  const labelClass = "block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5";

  // ══════════════════ SINGLE GIFT VIEW (unchanged) ══════════════════
  if (singleGift && !isCartMode) {
    return (
      <div className="animate-fadeIn max-w-3xl">
        <Link to="/dashboard/gifts" className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 font-medium mb-4 hover:underline">
          <ArrowLeft className="w-4 h-4" /> Back to Gifts
        </Link>

        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Checkout</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Complete the details below to send this gift.</p>

        <div className="flex items-center gap-4 bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 mb-6">
          <img src={singleGift.imageUrl} alt={singleGift.title} className="w-20 h-20 rounded-lg object-cover shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase">{singleGift.title}</p>
            <p className="text-base font-bold text-purple-600 dark:text-purple-400 mt-0.5">
              <Currency amount={singleGift.priceNgn} />
            </p>
            {singleGift.deliveryNote && (
              <p className="text-[11px] text-gray-400 uppercase tracking-wide font-medium mt-1">{singleGift.deliveryNote}</p>
            )}
          </div>
        </div>

        <form onSubmit={handleSingleSubmit} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-5 sm:p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">Order summary</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Sender Name</label>
              <input type="text" value={singleForm.senderName} onChange={setSingle("senderName")} placeholder="Your full name" required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Receiver Name</label>
              <input type="text" value={singleForm.receiverName} onChange={setSingle("receiverName")} placeholder="Recipient name" required className={inputClass} />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Address</label>
              <input type="text" value={singleForm.address} onChange={setSingle("address")} placeholder="Street address" required className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>City</label>
              <input type="text" value={singleForm.city} onChange={setSingle("city")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input type="text" value={singleForm.state} onChange={setSingle("state")} required className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Zip Code</label>
              <input type="text" value={singleForm.zipCode} onChange={setSingle("zipCode")} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Phone Number</label>
              <input type="tel" value={singleForm.phoneNumber} onChange={setSingle("phoneNumber")} required className={inputClass} />
            </div>

            <div>
              <label className={labelClass}>Country</label>
              <input type="text" value={singleForm.country} onChange={setSingle("country")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Apartment Number (Optional)</label>
              <input type="text" value={singleForm.apartmentNumber} onChange={setSingle("apartmentNumber")} className={inputClass} />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Note</label>
              <textarea
                value={singleForm.note}
                onChange={setSingle("note")}
                placeholder="Specify any features of the gift e.g. size, color, message on card..."
                rows={3}
                className={`${inputClass} resize-none`}
              />
            </div>

            <div className="sm:col-span-2">
              <label className={labelClass}>Image</label>
              {!imagePreview ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 cursor-pointer hover:border-purple-400 transition"
                >
                  <Upload className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">Choose an image to attach</span>
                </div>
              ) : (
                <div className="relative w-24 h-24">
                  <img src={imagePreview} alt="preview" className="w-24 h-24 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                  <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
              <p className="text-xs text-gray-400 mt-1.5">
                Attach an image for the gift (e.g. pictures for frames, custom hoodie design, etc.).
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 pt-5 border-t border-gray-100 dark:border-gray-800">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Total: <span className="font-bold text-gray-900 dark:text-gray-100"><Currency amount={singleGift.priceNgn} /></span>
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-semibold rounded-lg transition"
            >
              {submitting ? "Placing order..." : `Pay ${format(singleGift.priceNgn)}`}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // ══════════════════ CART CHECKOUT VIEW ══════════════════
  return (
    <div className="animate-fadeIn max-w-3xl">
      <Link to="/dashboard/cart" className="inline-flex items-center gap-1.5 text-sm text-purple-600 dark:text-purple-400 font-medium mb-4 hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to Cart
      </Link>

      <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Checkout</h1>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        You're purchasing {cart.length} gift{cart.length > 1 ? "s" : ""}. Complete the details below.
      </p>

      <div className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-4 mb-6 space-y-3">
        {cart.map(({ gift, quantity }) => (
          <div key={gift._id} className="flex items-center gap-3">
            <img src={gift.imageUrl} alt={gift.title} className="w-14 h-14 rounded-lg object-cover shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate uppercase">{gift.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Qty: {quantity}</p>
            </div>
            <p className="text-sm font-bold text-purple-600 dark:text-purple-400 shrink-0">
              <Currency amount={gift.priceNgn * quantity} />
            </p>
          </div>
        ))}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Total</p>
          <p className="text-base font-bold text-purple-600 dark:text-purple-400"><Currency amount={cartTotal} /></p>
        </div>
      </div>

      <form onSubmit={handleCartSubmit} className="bg-white dark:bg-[#0f172a] border border-gray-200 dark:border-gray-800 rounded-xl p-5 sm:p-6 space-y-5">
        <div>
          <label className={labelClass}>Sender Name</label>
          <input type="text" value={senderName} onChange={(e) => setSenderName(e.target.value)} placeholder="Your full name" required className={inputClass} />
        </div>

        <div>
          <label className={labelClass}>Where are these gifts going?</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setAddressMode("same")}
              className={`flex items-start gap-2.5 text-left p-3.5 rounded-lg border-2 transition ${
                addressMode === "same"
                  ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${addressMode === "same" ? "text-purple-600" : "text-gray-400"}`} />
              <span>
                <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Same person / address</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">All gifts go to one recipient</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setAddressMode("different")}
              className={`flex items-start gap-2.5 text-left p-3.5 rounded-lg border-2 transition ${
                addressMode === "different"
                  ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <Users className={`w-4 h-4 mt-0.5 shrink-0 ${addressMode === "different" ? "text-purple-600" : "text-gray-400"}`} />
              <span>
                <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100">Different people / addresses</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Each gift goes to a different recipient</span>
              </span>
            </button>
          </div>
        </div>

        {addressMode === "same" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
            <div>
              <label className={labelClass}>Receiver Name</label>
              <input type="text" value={sameAddressForm.receiverName} onChange={setSame("receiverName")} placeholder="Recipient name" required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Phone Number</label>
              <input type="tel" value={sameAddressForm.phoneNumber} onChange={setSame("phoneNumber")} required className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Address</label>
              <input type="text" value={sameAddressForm.address} onChange={setSame("address")} placeholder="Street address" required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>City</label>
              <input type="text" value={sameAddressForm.city} onChange={setSame("city")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>State</label>
              <input type="text" value={sameAddressForm.state} onChange={setSame("state")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Zip Code</label>
              <input type="text" value={sameAddressForm.zipCode} onChange={setSame("zipCode")} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Country</label>
              <input type="text" value={sameAddressForm.country} onChange={setSame("country")} required className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Apartment Number (Optional)</label>
              <input type="text" value={sameAddressForm.apartmentNumber} onChange={setSame("apartmentNumber")} className={inputClass} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass}>Note</label>
              <textarea value={sameAddressForm.note} onChange={setSame("note")} rows={3} placeholder="Any notes for all gifts..." className={`${inputClass} resize-none`} />
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            {cart.map(({ gift }) => {
              const f = itemForms[gift._id] || {};
              return (
                <div key={gift._id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2.5 mb-3">
                    <img src={gift.imageUrl} alt={gift.title} className="w-10 h-10 rounded object-cover" />
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase">{gift.title}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input type="text" value={f.receiverName || ""} onChange={setItemField(gift._id, "receiverName")} placeholder="Receiver name" required className={inputClass} />
                    <input type="tel" value={f.phoneNumber || ""} onChange={setItemField(gift._id, "phoneNumber")} placeholder="Phone number" required className={inputClass} />
                    <input type="text" value={f.address || ""} onChange={setItemField(gift._id, "address")} placeholder="Street address" required className={`${inputClass} sm:col-span-2`} />
                    <input type="text" value={f.city || ""} onChange={setItemField(gift._id, "city")} placeholder="City" required className={inputClass} />
                    <input type="text" value={f.state || ""} onChange={setItemField(gift._id, "state")} placeholder="State" required className={inputClass} />
                    <input type="text" value={f.zipCode || ""} onChange={setItemField(gift._id, "zipCode")} placeholder="Zip code (optional)" className={inputClass} />
                    <input type="text" value={f.country || ""} onChange={setItemField(gift._id, "country")} placeholder="Country" required className={inputClass} />
                    <input type="text" value={f.apartmentNumber || ""} onChange={setItemField(gift._id, "apartmentNumber")} placeholder="Apartment number (optional)" className={inputClass} />
                    <textarea value={f.note || ""} onChange={setItemField(gift._id, "note")} placeholder="Note for this gift (optional)" rows={2} className={`${inputClass} sm:col-span-2 resize-none`} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div>
          <label className={labelClass}>Image (optional)</label>
          {!imagePreview ? (
            <div onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 cursor-pointer hover:border-purple-400 transition">
              <Upload className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500 dark:text-gray-400">Choose a reference image to attach</span>
            </div>
          ) : (
            <div className="relative w-24 h-24">
              <img src={imagePreview} alt="preview" className="w-24 h-24 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
              <button type="button" onClick={removeImage} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
        </div>

        <div className="flex items-center justify-between pt-5 border-t border-gray-100 dark:border-gray-800">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Total: <span className="font-bold text-gray-900 dark:text-gray-100"><Currency amount={cartTotal} /></span>
          </p>
          <button
            type="submit"
            disabled={submitting}
            className="px-6 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-sm font-semibold rounded-lg transition"
          >
            {submitting ? "Placing order..." : `Pay ${format(cartTotal)}`}
          </button>
        </div>
      </form>
    </div>
  );
}